import FutureOrderListItem from '@/components/OrdersModule/OrderListItem/FutureOrderListItem'
import InProgressOrderListItem from '@/components/OrdersModule/OrderListItem/InProgressOrderListItem'
import NewOrderListItem from '@/components/OrdersModule/OrderListItem/NewOrderListItem'
import OrdersListSectionHeader from '@/components/OrdersModule/OrdersTabs/OrdersListSectionHeader'
import { postponingEnabled } from '@/constants/orders'
import { Phase } from '@/features/orders/constants/app'
import { useAppSelector } from '@/hooks/redux'
import { ordersSelectors } from '@/redux/reducers/orders/selectors'
import { OrdersListPhase } from '@/types/ordersList'
import { FlashList, ListRenderItem } from '@shopify/flash-list'
import { FC, useCallback, useMemo } from 'react'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

enum ListItemType {
    SectionHeader,
    Order,
}

interface SectionHeader {
    type: ListItemType.SectionHeader
    phase: OrdersListPhase
}

interface Order {
    type: ListItemType.Order
    phase: OrdersListPhase
    uuid: string
}

type ListItem = SectionHeader | Order

const keyExtractor = (item: ListItem): string =>
    item.type === ListItemType.SectionHeader ? item.phase : item.uuid

const getItemType = (item: ListItem): ListItemType.SectionHeader | string =>
    item.type === ListItemType.SectionHeader ? item.type : `${item.type}-${item.phase}`

const OrdersList: FC = () => {
    const newOrderUuids = useAppSelector(ordersSelectors.newOrderUuids)
    const inProgressOrderUuids = useAppSelector(ordersSelectors.inProgressOrderUuids)
    const futureOrderUuids = useAppSelector(ordersSelectors.futureOrderUuids)
    const selectedOrderUuid = useAppSelector(ordersSelectors.selectedOrderUuid)

    const data: ListItem[] = useMemo(
        () => [
            { type: ListItemType.SectionHeader, phase: Phase.New },
            ...newOrderUuids.map((uuid) => ({
                type: ListItemType.Order,
                phase: Phase.New as const,
                uuid,
            })),
            { type: ListItemType.SectionHeader, phase: Phase.InProgress },
            ...inProgressOrderUuids.map((uuid) => ({
                type: ListItemType.Order,
                phase: Phase.InProgress as const,
                uuid,
            })),
            ...(postponingEnabled
                ? [
                      {
                          type: ListItemType.SectionHeader,
                          phase: Phase.Future,
                      } as ListItem,
                      ...futureOrderUuids.map(
                          (uuid): ListItem => ({
                              type: ListItemType.Order,
                              phase: Phase.Future as const,
                              uuid,
                          })
                      ),
                  ]
                : []),
        ],
        [newOrderUuids, inProgressOrderUuids, futureOrderUuids]
    )

    const stickyHeaderIndices = useMemo(
        () =>
            data
                .map((item, index) => {
                    if (item.type === ListItemType.SectionHeader) {
                        return index
                    } else {
                        return null
                    }
                })
                .filter((item) => item !== null),
        [data]
    )

    const { styles } = useStyles(stylesheet)

    const renderItem: ListRenderItem<ListItem> = useCallback(
        ({ item, index }) => {
            if (item.type === ListItemType.SectionHeader) {
                return (
                    <OrdersListSectionHeader
                        phase={item.phase}
                        style={index === 0 && styles.firstSectionHeader}
                    />
                )
            }

            const isSelected = item.uuid === selectedOrderUuid

            switch (item.phase) {
                case Phase.New:
                    return <NewOrderListItem uuid={item.uuid} selected={isSelected} />
                case Phase.InProgress:
                    return <InProgressOrderListItem uuid={item.uuid} selected={isSelected} />
                case Phase.Future:
                    return <FutureOrderListItem uuid={item.uuid} selected={isSelected} />
                default:
                    throw new Error(`Unknown order item phase '${item.phase}'`)
            }
        },
        [selectedOrderUuid, styles]
    )

    return (
        <FlashList
            data={data}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            extraData={selectedOrderUuid}
            estimatedItemSize={64}
            getItemType={getItemType}
            stickyHeaderIndices={stickyHeaderIndices}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.contentContainer}
        />
    )
}

const stylesheet = createStyleSheet((_, runtime) => ({
    firstSectionHeader: {
        paddingTop: 8,
    },
    contentContainer: {
        paddingBottom: runtime.insets.bottom,
    },
}))

export default OrdersList
